import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, ReactNode } from 'react'

import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes'
import { VariantProps, cva } from 'class-variance-authority'
import clsx from 'clsx'

import { ActionLink } from '../ActionLink/ActionLink'
import { Heading } from '../Heading/Heading'
import { cn } from '../utils'

export type ModalProps = {
  /** Whether the Dialog is open or not. */
  open: boolean
  /** Called when the Dialog is dismissed (via the overlay or Escape key). Typically used to close the dialog by setting open to false. */
  setIsOpen: (open: boolean) => void
  /** A ref to an element that should receive focus first. */
  initialFocus?: React.MutableRefObject<HTMLElement>
  /** Whether the element should ignore the internally managed open/closed state. */
  static?: boolean
  /** Whether the element should be unmounted or hidden based on the open/closed state. */
  unmount?: boolean
  /** Adds banner image at the top of the modal */
  illustrationSrc?: string
  /** Modal Title */
  heading?: string | React.ReactElement | Array<React.ReactElement>
  headingWrapperClassName?: string
  hiddenCloseBtn?: boolean
  closeOnClickOutside?: boolean
  /** Content will have no background and the box shadow around the modal is removed */
  transparentMode?: boolean
  children?: ReactNode | undefined
} & VariantProps<typeof modalVariants>

const modalVariants = cva('', {
  defaultVariants: {
    theme: 'default',
    width: 'lg'
  },
  variants: {
    theme: {
      center: 'text-center flex-col flex items-center',
      default: 'text-left'
    },
    width: {
      lg: 'md:max-w-lg',
      xl: 'md:max-w-xl',
      xxl: 'md:max-w-2xl'
    }
  }
})

/**
 * Modal - A dialog is a window overlaid on either the primary window or another dialog window. Content behind a modal dialog is inert, meaning that users cannot interact with it.
 */
export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    children,
    closeOnClickOutside = true,
    heading,
    headingWrapperClassName,
    hiddenCloseBtn = false,
    illustrationSrc,
    open = false,
    setIsOpen,
    theme,
    transparentMode = false,
    unmount = false,
    width
  }: Readonly<ModalProps>,
  ref
) {
  return (
    <Transition.Root as={Fragment} show={open}>
      <Dialog
        as='div'
        className='fixed inset-0 z-50 overflow-y-auto'
        onClose={() => {
          if (!closeOnClickOutside) return
          setIsOpen(false)
        }}
        unmount={unmount}
      >
        <div className='flex min-h-screen flex-col items-stretch justify-center px-2 text-center md:block md:pb-20 md:pt-4'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-black bg-opacity-75 transition-opacity' />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span aria-hidden='true' className='hidden md:inline-block md:h-screen md:align-middle'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <Dialog.Panel
              className={clsx(
                'm-2 transform rounded-md transition-all sm:m-0 md:inline-block md:w-full md:align-middle',
                transparentMode ? 'bg-transparent shadow-none' : 'bg-background-default shadow-lg',
                modalVariants({ width })
              )}
              ref={ref}
            >
              {illustrationSrc ? (
                <div
                  className='flex min-h-64 flex-col justify-end overflow-hidden rounded-t-md bg-cover bg-center p-6 text-left antialiased'
                  style={{ backgroundImage: `url(${illustrationSrc})` }}
                >
                  {heading ? <h2 className='text-3xl font-bold text-white'>{heading}</h2> : null}
                </div>
              ) : heading ? (
                <div
                  className={cn(
                    'flex flex-wrap items-center justify-between px-6 py-4 sm:flex-nowrap',
                    headingWrapperClassName
                  )}
                >
                  <div></div>
                  <Heading theme='section'>{heading}</Heading>
                  {!hiddenCloseBtn ? (
                    <ActionLink
                      icon={faTimes}
                      onClick={() => {
                        setIsOpen(false)
                      }}
                      theme='white'
                    />
                  ) : null}
                </div>
              ) : (
                !hiddenCloseBtn && (
                  <div className='px-6 pt-6 text-right'>
                    <ActionLink
                      icon={faTimes}
                      onClick={() => {
                        setIsOpen(false)
                      }}
                    />
                  </div>
                )
              )}
              <div className={clsx('p-6', modalVariants({ theme }))}>{children}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
})
